import { useState } from "react";
import { Clock, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { useLang } from "../../lib/i18n";
import { Avatar } from "./workMeta";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";

export const fmtH = (h) => (h == null ? "—" : `${Number(h) % 1 === 0 ? Number(h) : Number(h).toFixed(1)}h`);

export function fmtMoney(v, currency) {
  if (v == null) return "—";
  const cur = currency || "IDR";
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: cur, maximumFractionDigits: cur === "IDR" || cur === "JPY" ? 0 : 2 }).format(v);
  } catch { return `${cur} ${v}`; }
}

const today = () => new Date().toISOString().slice(0, 10);

export function LogWorkDialog({ open, onOpenChange, item, onLogged }) {
  const { t } = useLang();
  const [form, setForm] = useState({ hours: "", work_date: today(), note: "", remaining_hours: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/work-items/${item.key}/worklogs`, {
        hours: Number(form.hours), work_date: form.work_date || null, note: form.note || null,
        remaining_hours: form.remaining_hours === "" ? null : Number(form.remaining_hours),
      });
      toast.success(t("workLogged"));
      setForm({ hours: "", work_date: today(), note: "", remaining_hours: "" });
      onOpenChange(false); onLogged(data);
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  const autoRemaining = item.remaining_hours != null ? Math.max(item.remaining_hours - (Number(form.hours) || 0), 0) : item.estimate_hours != null ? Math.max(item.estimate_hours - (Number(form.hours) || 0), 0) : null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" aria-describedby={undefined} data-testid="log-work-dialog">
        <DialogHeader><DialogTitle className="font-heading">{t("logWork")} · {item.key}</DialogTitle></DialogHeader>
        <form className="space-y-3" onSubmit={submit}>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1"><Label>{t("hoursSpent")}</Label>
              <Input autoFocus type="number" step="0.25" min="0.25" max="24" data-testid="log-work-hours" value={form.hours} onChange={set("hours")} required /></div>
            <div className="space-y-1"><Label>{t("workDate")}</Label>
              <Input type="date" data-testid="log-work-date" value={form.work_date} onChange={set("work_date")} /></div>
          </div>
          <div className="space-y-1"><Label>{t("newRemaining")} <span className="text-muted-foreground">({t("autoRemaining")}: {fmtH(autoRemaining)})</span></Label>
            <Input type="number" step="0.25" min="0" data-testid="log-work-remaining" value={form.remaining_hours} onChange={set("remaining_hours")} placeholder={autoRemaining != null ? String(autoRemaining) : "—"} /></div>
          <div className="space-y-1"><Label>{t("note")}</Label>
            <Textarea rows={2} data-testid="log-work-note" value={form.note} onChange={set("note")} maxLength={500} /></div>
          <Button type="submit" data-testid="log-work-submit" className="w-full bg-[#1E3A8A] hover:bg-[#1c3479]" disabled={busy || !(Number(form.hours) > 0)}>{busy ? "..." : t("logWork")}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function TimeTracking({ item, canEdit, onLogged }) {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const logged = Number(item.logged_hours || 0);
  const est = item.estimate_hours != null ? Number(item.estimate_hours) : null;
  const rem = item.remaining_hours != null ? Number(item.remaining_hours) : null;
  const total = est != null ? Math.max(est, logged + (rem ?? 0)) : logged + (rem ?? 0);
  const pctLogged = total > 0 ? Math.min(100, Math.round((logged / total) * 100)) : 0;
  const over = est != null && logged > est;
  return (
    <div className="space-y-1.5 py-1" data-testid="time-tracking">
      <div className="flex items-center justify-between text-xs">
        <span className="text-muted-foreground inline-flex items-center gap-1"><Clock className="h-3 w-3" />{t("timeTracking")}</span>
        {canEdit && <button type="button" data-testid="log-work-btn" onClick={() => setOpen(true)} className="inline-flex items-center gap-1 text-[#1E3A8A] hover:underline"><Plus className="h-3 w-3" />{t("logWork")}</button>}
      </div>
      <div className="h-2 rounded-full bg-secondary overflow-hidden" title={`${pctLogged}%`}>
        <div className={`h-full transition-[width] ${over ? "bg-[#DC2626]" : "bg-[#1E3A8A]"}`} style={{ width: `${pctLogged}%` }} data-testid="time-tracking-bar" />
      </div>
      <div className="flex justify-between text-[11px] text-muted-foreground">
        <span data-testid="tt-logged"><b className="text-foreground">{fmtH(logged)}</b> {t("loggedShort")}</span>
        <span data-testid="tt-remaining"><b className="text-foreground">{fmtH(rem)}</b> {t("remainingShort")}</span>
        <span data-testid="tt-estimate"><b className="text-foreground">{fmtH(est)}</b> {t("estimateShort")}</span>
      </div>
      <LogWorkDialog open={open} onOpenChange={setOpen} item={item} onLogged={onLogged} />
    </div>
  );
}

export function WorklogList({ worklogs, canManage, meId, onDeleted, itemKey }) {
  const { t } = useLang();
  const remove = async (id) => {
    try { const { data } = await api.delete(`/work-items/${itemKey}/worklogs/${id}`); toast.success(t("worklogRemoved")); onDeleted(data); }
    catch (e) { toast.error(apiError(e)); }
  };
  const total = worklogs.reduce((a, w) => a + Number(w.hours || 0), 0);
  return (
    <div className="divide-y border rounded-md text-sm" data-testid="worklog-list">
      {worklogs.map((w) => (
        <div key={w.id} className="flex items-start gap-3 px-3 py-2" data-testid={`worklog-row-${w.id}`}>
          <Avatar name={w.user_name} />
          <div className="flex-1 min-w-0">
            <div className="text-xs"><span className="font-medium">{w.user_name}</span> · {w.work_date} · <b>{fmtH(w.hours)}</b></div>
            {w.note && <div className="text-xs text-muted-foreground mt-0.5 whitespace-pre-wrap">{w.note}</div>}
          </div>
          {(canManage || w.user_id === meId) && <button type="button" data-testid={`worklog-delete-${w.id}`} onClick={() => remove(w.id)} className="text-muted-foreground hover:text-[#991B1B]" title={t("remove")}><Trash2 className="h-3.5 w-3.5" /></button>}
        </div>
      ))}
      {worklogs.length === 0 && <div className="px-3 py-2 text-xs text-muted-foreground">{t("noWorklogs")}</div>}
      {worklogs.length > 0 && <div className="px-3 py-1.5 text-xs text-muted-foreground bg-secondary/40">{t("total")}: <b className="text-foreground">{fmtH(total)}</b></div>}
    </div>
  );
}
